import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useDataTable,
    useDataTableExpansion,
    type DataTableFilter,
    type DataTableRefs
} from '@odo/shared-ui';
import { useServerPatients, useInfinitePatients } from '../../../../hooks/useServerPatients';
import { useMeta } from '../../../../hooks/useWorkQueue';
import { useStatusCatalog } from '../../../../services/statusCatalog';
import { DATA_TABLE_ROW_HEIGHT } from '../../../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../../../hooks/useViewportTableLimit';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { PatientsTable } from './PatientsTable';
import type { PatientsSortBy } from './patientColumns';
import { joinPatientWithStatus, type PracticePatientRow } from './patientStatusJoin';
import {
    buildPhaseOptions,
    groupStatusesByPhase,
    resolveInternalStatusFilter
} from './internalStatusFilter';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

interface PatientsTabProps {
    // /v2/patients filters by practice NAME only (no id-based filter server-side) -
    // fragile against a rename race, but a pre-existing endpoint constraint.
    practiceName: string;
}

// Two client-side filters, one server param (both resolve through
// resolveInternalStatusFilter) - separate keys so neither pill stomps the other.
type PracticePatientsFilters = { phase: string[]; internalStep: string[] };

// The parent only mounts this while the Patients tab is active (see
// PracticeDetail.tsx), so every hook below is free to fetch unconditionally.
export function PatientsTab({ practiceName }: PatientsTabProps) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Patient is the only sortable column (server `sort: 'patient'`) - nothing
    // here was ever persisted, so no `storageKey`/`filterStorageKeys`.
    const table = useDataTable<PatientsSortBy, PracticePatientsFilters>({
        defaultSort: { by: 'patient', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { phase: [], internalStep: [] }
    });
    const { phase: phaseFilter, internalStep: stepFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';
    const expansion = useDataTableExpansion();

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef,
        remeasure
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    // Expanding moves the card between the tab pane and a full-screen overlay -
    // re-measure so a paged table's page size reflects the new height.
    useLayoutEffect(() => {
        remeasure();
    }, [expansion.expanded, remeasure]);

    const { data: workflowMeta } = useMeta();
    const catalog = useStatusCatalog();
    const statuses = useMemo(() => workflowMeta?.statuses ?? [], [workflowMeta?.statuses]);
    const phases = useMemo(() => workflowMeta?.phases ?? [], [workflowMeta?.phases]);

    const internalStatus = useMemo(
        () => resolveInternalStatusFilter(phaseFilter, stepFilter, statuses),
        [phaseFilter, stepFilter, statuses]
    );

    const sharedPatientParams = {
        practice: [practiceName],
        sort: table.sort,
        order: table.order,
        search: table.search || undefined,
        internalStatus
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - same split as InvoicesContent's paged/infinite pair.
    const paged = useServerPatients({
        ...sharedPatientParams,
        page: table.page,
        limit: table.limit,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfinitePatients({
        ...sharedPatientParams,
        limit: table.limit,
        enabled: isInfinite
    });

    const patients = isInfinite ? infinite.patients : paged.patients;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isError = isInfinite ? infinite.isError : paged.isError;
    const error = isInfinite ? infinite.error : paged.error;
    // Invalidates by the shared `PATIENTS_KEY` prefix, so it refreshes whichever
    // of the two queries is active regardless of which issued it.
    const { refresh } = paged;

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load patients'), 'error');
        }
    }, [isError, error, showToast]);

    const rows: PracticePatientRow[] = useMemo(
        () => patients.map((patient) => joinPatientWithStatus(patient, catalog)),
        [patients, catalog]
    );

    const totalPages = Math.max(1, Math.ceil(meta.total / table.limit));
    const hasFilters = phaseFilter.length > 0 || stepFilter.length > 0;
    const clearFilters = () => {
        table.setFilter('phase', []);
        table.setFilter('internalStep', []);
    };

    const openPatient = (patient: PracticePatientRow) => {
        navigate(`/patients/${patient.id}`, { state: { patientName: patient.name } });
    };

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    const phaseOptions = useMemo(() => buildPhaseOptions(phases), [phases]);
    // Sectioned like the Work Queue's phase headings (GroupNav/StatsBar) rather
    // than one flat list - `searchable` keeps a known label one keystroke away.
    const stepGroups = useMemo(
        () => groupStatusesByPhase(statuses, phases, catalog),
        [statuses, phases, catalog]
    );
    const filters: DataTableFilter[] = [
        {
            key: 'phase',
            label: 'Phase',
            multiple: true,
            options: phaseOptions,
            value: phaseFilter,
            onChange: (value) => table.setFilter('phase', value),
            align: 'right'
        },
        {
            key: 'internalStep',
            label: 'Internal step',
            align: 'right',
            searchable: true,
            sections: stepGroups.map((group) => ({
                key: group.phase,
                label: group.phase,
                multiple: true,
                options: group.steps,
                // Every section shares the same value/onChange - one filter
                // grouped by phase for readability, not several independent ones.
                value: stepFilter,
                onChange: (value: string[]) => table.setFilter('internalStep', value)
            }))
        }
    ];

    return (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
            <PatientsTable
                patients={rows}
                isLoading={isLoading}
                isError={isError}
                search={table.search}
                searchConfig={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search by patient name'
                }}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                filters={filters}
                onRetry={() => void refresh()}
                onOpenPatient={openPatient}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                refs={refs}
                expansion={expansion}
            />
        </div>
    );
}
